<template>
  <aside class="side-panel">
    <h2 class="side-title">{{ t('side.title') }}</h2>
    <div class="io-toolbar">
      <button
        type="button"
        class="io-btn"
        :disabled="!canExport"
        @click="emit('export-game')"
      >
        {{ t('side.exportJson') }}
      </button>
      <button
        type="button"
        class="io-btn"
        @click="emit('import-game')"
      >
        {{ t('side.importJson') }}
      </button>
      <button
        type="button"
        class="io-btn"
        :disabled="!canExport"
        @click="emit('save-local')"
      >
        {{ t('side.saveLocal') }}
      </button>
    </div>

    <div class="local-saves-block">
      <h3 class="local-saves-title">{{ t('side.savedBrowserTitle') }}</h3>
      <p v-if="!localSaves.length" class="local-saves-empty">
        {{ t('side.saveListEmpty') }}
      </p>
      <ul v-else class="local-saves-list" :aria-label="t('side.savedBrowserTitle')">
        <li
          v-for="s in localSaves"
          :key="s.id"
          class="local-save-li"
        >
          <div class="local-save-top">
            <span class="local-save-label">{{ s.label }}</span>
            <div class="local-save-actions">
              <button
                type="button"
                class="io-btn io-btn--compact"
                @click="emit('load-local-save', s.id)"
              >
                {{ t('side.reviewBrowserSave') }}
              </button>
              <button
                type="button"
                class="io-btn io-btn--compact"
                @click="emit('remove-local-save', s.id)"
              >
                {{ t('side.deleteBrowserSave') }}
              </button>
            </div>
          </div>
          <span class="local-save-meta">{{ formatSavedLine(s) }}</span>
        </li>
      </ul>
    </div>

    <p class="side-legend">
      {{ t('side.legendBoard') }}
      <template v-if="gameOver && moveLog.length">
        {{ t('side.legendReplay') }}
      </template>
    </p>
    <ol class="move-list" :aria-label="t('side.moveListAria')">
      <li
        v-if="gameOver && moveLog.length"
        class="move-li"
      >
        <button
          type="button"
          class="move-row move-row--btn"
          :class="{ 'move-row--active': replayCursor === 0 }"
          @click="emitReplay(0)"
        >
          <span class="move-no">{{ t('side.initial') }}</span>
          <span class="move-detail">{{ t('side.initialHint') }}</span>
        </button>
      </li>
      <li
        v-for="m in moveLog"
        :key="m.serial"
        class="move-li"
      >
        <button
          v-if="gameOver"
          type="button"
          class="move-row move-row--btn"
          :class="{ 'move-row--active': replayCursor === m.serial }"
          @click="emitReplay(m.serial)"
        >
          <span class="move-no">{{ t('side.moveNo', { n: m.serial }) }}</span>
          <span class="move-detail">
            {{ actorLabel(m.player) }} · {{ m.square }}
            <span v-if="m.flipCount" class="flip-count">{{ t('side.flipCount', { c: m.flipCount }) }}</span>
          </span>
        </button>
        <div v-else class="move-row">
          <span class="move-no">{{ t('side.moveNo', { n: m.serial }) }}</span>
          <span class="move-detail">
            {{ actorLabel(m.player) }} · {{ m.square }}
            <span v-if="m.flipCount" class="flip-count">{{ t('side.flipCount', { c: m.flipCount }) }}</span>
          </span>
        </div>
      </li>
    </ol>

    <div v-if="gameOver" class="final-block">
      <h3 class="final-title">{{ t('side.finalTitle') }}</h3>
      <p class="final-result">{{ resultTitle }}</p>
      <p class="final-score">{{ t('side.scoreLine', { b: scores.black, w: scores.white }) }}</p>
      <div class="final-actions">
        <button type="button" class="btn" @click="emit('play-again')">
          {{ t('side.playAgain') }}
        </button>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed } from 'vue'
import { BLACK } from '@/composables'
import { locale, t } from '@/i18n/index.js'

const props = defineProps({
  moveLog: {
    type: Array,
    default: () => []
  },
  gameOver: {
    type: Boolean,
    default: false
  },
  scores: {
    type: Object,
    required: true
  },
  resultTitle: {
    type: String,
    default: ''
  },
  replayCursor: {
    type: Number,
    default: 0
  },
  localSaves: {
    type: Array,
    default: () => []
  },
  blackDisplayName: {
    type: String,
    default: ''
  },
  whiteDisplayName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'play-again',
  'replay-step',
  'export-game',
  'import-game',
  'save-local',
  'load-local-save',
  'remove-local-save'
])

const canExport = computed(() => props.moveLog.length > 0)

function emitReplay(step) {
  emit('replay-step', step)
}

function actorLabel(player) {
  return player === BLACK
    ? props.blackDisplayName
    : props.whiteDisplayName
}

/** @param {{ savedAt?: string, moveCount?: number }} s */
function formatSavedLine(s) {
  void locale.value
  const mc = t('side.savedMoveCount', { n: s.moveCount ?? 0 })
  if (!s.savedAt) return mc
  const d = new Date(s.savedAt)
  if (Number.isNaN(d.getTime())) return mc
  return `${mc} · ${d.toLocaleString()}`
}
</script>

<style scoped>
.side-panel {
  box-sizing: border-box;
  flex: 1 1 0;
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: var(--bg-medium, #1f2937);
  border-radius: var(--radius-md, 10px);
  border: 1px solid rgba(148, 163, 184, 0.15);
  overflow: hidden;
}

.side-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--accent-primary, #22c55e);
  margin: 0;
  flex-shrink: 0;
}

.io-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
}

.io-btn {
  flex: 1 1 auto;
  min-width: 0;
  padding: 6px 10px;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.55);
  color: var(--text-white, #f1f5f9);
  cursor: pointer;
  transition:
    background 0.12s,
    opacity 0.12s;
}

.io-btn:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.45);
}

.io-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.io-btn--compact {
  flex: 0 1 auto;
  padding: 5px 8px;
  font-size: 0.72rem;
}

.local-saves-block {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
}

.local-saves-title {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--text-gray, #94a3b8);
  text-transform: uppercase;
}

.local-saves-empty {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--text-gray, #94a3b8);
  opacity: 0.92;
}

.local-saves-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: min(200px, 28vh);
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.local-save-li {
  margin: 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.local-save-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
}

.local-save-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-white, #f1f5f9);
  word-break: break-word;
  flex: 1 1 auto;
  min-width: 0;
}

.local-save-meta {
  font-size: 0.7rem;
  color: var(--text-gray, #94a3b8);
  line-height: 1.35;
}

.local-save-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.side-legend {
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--text-gray, #94a3b8);
  opacity: 0.95;
  flex-shrink: 0;
}

.ul-sample {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.move-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  flex: 1 1 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.82rem;
  color: var(--text-gray, #94a3b8);
}

.move-li {
  margin: 0;
  padding: 0;
}

.move-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  text-align: left;
}

.move-li:last-child .move-row {
  border-bottom: none;
  padding-bottom: 0;
}

.move-row--btn {
  margin: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm, 6px);
  padding: 6px 8px;
  margin: -6px -8px;
  width: calc(100% + 16px);
  box-sizing: border-box;
  transition: background 0.12s;
}

.move-row--btn:hover {
  background: rgba(148, 163, 184, 0.1);
}

.move-row--active {
  background: rgba(34, 197, 94, 0.15);
  outline: 1px solid rgba(34, 197, 94, 0.45);
}

.move-row--btn.move-row--active:hover {
  background: rgba(34, 197, 94, 0.2);
}

.move-no {
  font-weight: 600;
  color: var(--text-white, #f1f5f9);
  font-size: 0.8rem;
}

.move-detail {
  padding-left: 2px;
  line-height: 1.35;
}

.flip-count {
  display: block;
  margin-top: 2px;
  font-size: 0.75rem;
  opacity: 0.9;
}

.final-block {
  flex-shrink: 0;
  padding-top: 10px;
  border-top: 1px solid rgba(34, 197, 94, 0.35);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.final-title {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--text-gray, #94a3b8);
  text-transform: uppercase;
}

.final-result {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--accent-primary, #22c55e);
}

.final-score {
  margin: 0;
  font-size: 0.88rem;
  color: var(--text-gray, #94a3b8);
}

.final-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.btn {
  padding: 8px 16px;
  background: var(--accent-primary, #22c55e);
  color: #fff;
  border: none;
  border-radius: var(--radius-sm, 6px);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.2s;
  align-self: flex-start;
}

.btn:hover {
  opacity: 0.88;
}

.btn--outline {
  background: transparent;
  color: var(--accent-primary, #22c55e);
  border: 2px solid var(--accent-primary, #22c55e);
}

.btn--outline:hover {
  background: rgba(34, 197, 94, 0.12);
  opacity: 1;
}

@media (max-width: 900px) {
  .side-panel {
    min-height: auto;
    overflow: visible;
  }

  .move-list {
    flex: none;
    min-height: 140px;
    max-height: min(42vh, 320px);
  }
}
</style>
